import { Trash2 } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { EmailCampaign } from "./email";

/** A campaign still going out has to be stopped before it can be deleted. */
export const canDeleteCampaign = (campaign: Pick<EmailCampaign, "status">) =>
  !["sending", "scheduled"].includes(campaign.status);

/**
 * Deletes a campaign (draft, sent or cancelled) after asking. Its numbers
 * (sends, opens, clicks) go with it; unsubscribes and the notes left on the
 * contacts stay.
 */
export const DeleteCampaignButton = ({
  campaign,
  onDeleted,
  compact,
}: {
  campaign: Pick<EmailCampaign, "id" | "name" | "status">;
  onDeleted: () => void;
  /** Icon only (for table rows) */
  compact?: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!canDeleteCampaign(campaign)) return null;
  const isDraft = campaign.status === "draft";

  const remove = async () => {
    setBusy(true);
    try {
      await dataProvider.delete("gula_email_campaigns", {
        id: campaign.id,
        previousData: campaign,
      });
      notify(`«${campaign.name}» borrada`, { type: "info" });
      setOpen(false);
      onDeleted();
    } catch (error) {
      console.error(error);
      notify("No se ha podido borrar", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          title="Borrar campaña"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ) : (
        <Button
          variant="outline"
          className={cn("hover:text-destructive")}
          onClick={() => setOpen(true)}
        >
          <Trash2 className="h-4 w-4" />
          Borrar
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onClick={(event) => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>¿Borrar «{campaign.name}»?</DialogTitle>
            <DialogDescription>
              {isDraft
                ? "Es un borrador: no se ha enviado a nadie."
                : "Se borran también sus números (enviados, aperturas y clics). Las bajas y las notas en las fichas se quedan."}{" "}
              No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={remove} disabled={busy}>
              <Trash2 className="h-4 w-4" />
              Sí, borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
